import { useState } from 'react'
import { DocumentNode, useMutation, useSubscription } from '@apollo/client'
import { UploadProps, Upload, Alert, Button, Typography, Space } from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import { xlsxFileMimeType } from '../Constants/general'
import {
  InboxOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons'
import Spinner from './Spinner'

const { Dragger } = Upload

type Props = {
  /** The mutation for uploading the file */
  uploaderMutation: DocumentNode
  /** The subscription for listening changes afte the file upload */
  uploaderSubscription: DocumentNode
  /** The response key on the subscription response
   * It is expected that this key has an object with
   * `type` and `message` properties
   */
  responseKey: string
  sampleUrl: string
  /** Optional primary color to match theme */
  primaryColor?: string
}

type SampleFileProp = {
  sampleUrl: string
  primaryColor?: string
}

/**
 * @author Ayush Choubey
 * @since 28/05/2024
 * @description This uploader component is used at for uploading a file
 *              and listening for the subscription messages while file is being processed
 */
const SubscriptionFileUploader = ({
  uploaderMutation,
  uploaderSubscription,
  responseKey,
  sampleUrl,
  primaryColor = '#F2709C', // Default to match your app's gradient
}: Props) => {
  const [beforeUploadError, setBeforeUploadError] = useState<string>('')
  const [showSubscriptionMessage, setShowSubscriptionMessage] =
    useState<boolean>(false)

  const [productUpload, { loading: uploading }] = useMutation(uploaderMutation)

  const { data: subscriptionResponse } = useSubscription(uploaderSubscription)

  const uploadProps: UploadProps = {
    name: 'file',
    maxCount: 1,
    accept: xlsxFileMimeType,
    beforeUpload: () => false,
    onChange: (file: UploadChangeParam) => {
      if (validateFile(file)) {
        setShowSubscriptionMessage(() => true)
        uploadFile(file)
      }
    },
  }

  const validateFile = (file: UploadChangeParam): boolean | void => {
    setShowSubscriptionMessage(() => false)
    // Check if file was removed
    if (file.file.status === 'removed') {
      return setBeforeUploadError('')
    }
    // Check for file size
    const fileSizeGreaterThanTen: boolean =
      file?.file && file.file.size ? file.file.size / 1024 / 1024 > 10 : false
    if (fileSizeGreaterThanTen) {
      return setBeforeUploadError('Please add a file less than 10 MB')
    }
    // Check for file type
    if (file.file.type !== xlsxFileMimeType) {
      return setBeforeUploadError('Please add .xlsx file only')
    }
    setBeforeUploadError('')
    return true
  }

  const uploadFile = (file: UploadChangeParam): void => {
    productUpload({
      variables: {
        file: file.file,
      },
    })
  }

  return (
    <div
      className='subscription-uploader-container'
      style={{ maxWidth: '800px', margin: '0 auto' }}
    >
      <div className='row justify-content-center'>
        <div className='col-12'>
          <div
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Dragger {...uploadProps} style={{ marginBottom: '24px' }}>
              <p className='ant-upload-drag-icon'>
                <InboxOutlined
                  style={{ color: primaryColor, fontSize: '48px' }}
                />
              </p>
              <p
                className='ant-upload-text'
                style={{ fontSize: '16px', fontWeight: 500 }}
              >
                Click or drag Excel file to this area to upload
              </p>
              <p className='ant-upload-hint' style={{ color: '#888' }}>
                Support for .xlsx files only. Maximum file size: 10MB
              </p>
            </Dragger>

            <SampleFile sampleUrl={sampleUrl} primaryColor={primaryColor} />

            <div style={{ marginTop: '24px' }}>
              {uploading && <Spinner />}

              {!!beforeUploadError && (
                <Alert
                  type='error'
                  message={beforeUploadError}
                  style={{ marginTop: '16px' }}
                  showIcon
                />
              )}

              {!!subscriptionResponse && showSubscriptionMessage && (
                <Alert
                  type={subscriptionResponse[responseKey].type}
                  message={subscriptionResponse[responseKey].message}
                  style={{ marginTop: '16px' }}
                  showIcon
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SampleFile = ({ sampleUrl, primaryColor }: SampleFileProp) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      marginTop: '16px',
      padding: '16px',
      background: '#f9f9f9',
      borderRadius: '6px',
    }}
  >
    <Space>
      <FileExcelOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
      <a
        href={sampleUrl}
        className='sample-file-link'
        style={{
          display: 'flex',
          alignItems: 'center',
          color: primaryColor,
          fontWeight: 500,
          textDecoration: 'none',
        }}
      >
        <span>Download Sample File</span>
        <DownloadOutlined style={{ marginLeft: '8px' }} />
      </a>
    </Space>
  </div>
)

export default SubscriptionFileUploader
